import React from 'react';
import { useTranslation } from 'react-i18next';
import { type Appointment, type CalendarEvent, type Doctor, type Patient } from './types';
import { formatTime } from './utils';
import { useTheme } from '../../theme/ThemeContext';
import { formatDate } from '../../utils/dateFormat';

export interface CalendarSearchResultGroup {
  date: Date;
  items: (Appointment | CalendarEvent)[];
}

interface CalendarSearchResultsProps {
  query: string;
  loading: boolean;
  groups: CalendarSearchResultGroup[] | null;
  patients: Patient[];
  doctors: Doctor[];
  onClear: () => void;
  onSelectDate: (date: Date) => void;
}

export const CalendarSearchResults: React.FC<CalendarSearchResultsProps> = ({
  query,
  loading,
  groups,
  patients,
  doctors,
  onClear,
  onSelectDate
}) => {
  const { t, i18n } = useTranslation();
  const { tokens } = useTheme();

  return (
    <div style={{ background: tokens.app.surface, border: `1px solid ${tokens.app.border}`, borderRadius: '8px', height: '100%', overflowY: 'auto', padding: '24px', color: tokens.app.text }}>
      <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
          {t('calendar.searchResults', { query })}
          {loading && <span style={{ marginLeft: 12, fontSize: '0.85rem', color: tokens.app.muted, fontWeight: 400 }}>Buscando...</span>}
        </h3>
        <button onClick={onClear} style={{ padding: '8px 16px', background: tokens.app.surfaceAlt, color: tokens.app.text, border: `1px solid ${tokens.app.border}`, borderRadius: '6px', cursor: 'pointer' }}>
          {t('calendar.clearSearch')}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: tokens.app.muted }}>Buscando en todas las fechas...</div>
      ) : !groups || groups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: tokens.app.muted }}>{t('calendar.noMatches')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{ fontSize: '0.85rem', color: tokens.app.muted, marginBottom: '-16px' }}>
            {groups.reduce((count, group) => count + group.items.length, 0)} resultado(s) en {groups.length} fecha(s)
          </div>
          {groups.map(({ date, items }) => (
            <div key={date.toISOString()}>
              <h4 className="responsive-row" style={{ fontSize: '1.1rem', color: tokens.app.muted, borderBottom: `1px solid ${tokens.app.border}`, paddingBottom: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: tokens.app.text, fontWeight: 600 }}>{date.toLocaleDateString(i18n.language, { weekday: 'long' })}</span>
                <span>{formatDate(date)}</span>
              </h4>

              <div style={{ display: 'grid', gap: '12px' }}>
                {items.map(item => {
                  const isAppointment = 'patientId' in item;
                  const start = item.startTime ? new Date(item.startTime) : null;
                  const end = item.endTime ? new Date(item.endTime) : null;
                  const time = start && end ? `${formatTime(start)} - ${formatTime(end)}` : t('calendar.standBy');
                  let title = '';
                  let subtitle = '';
                  let hc: string | undefined;
                  let color = '#667eea';

                  if (isAppointment) {
                    const appointment = item as Appointment;
                    const patient = appointment.patient ?? patients.find(candidate => candidate.id === appointment.patientId);
                    title = patient?.name || appointment.prospectName || t('calendar.unknownPatient');
                    hc = patient?.hc;
                    if (appointment.isProspect) title = appointment.prospectName || t('calendar.newPatientIntake');

                    const doctor = doctors.find(candidate => candidate.id === appointment.doctorId);
                    const details = [];
                    if (doctor) details.push(`${t('calendar.dr')} ${doctor.firstName} ${doctor.lastName}`);
                    const linkedTreatmentNames = Array.isArray(appointment.treatments)
                      ? appointment.treatments.map(treatment => treatment?.name).filter(Boolean)
                      : [];
                    const reasonLabel = linkedTreatmentNames.length > 0
                      ? linkedTreatmentNames.join(', ')
                      : appointment.treatment?.name || appointment.treatmentTemplate?.name || appointment.customReason;
                    if (reasonLabel) details.push(reasonLabel);
                    subtitle = details.join(' \u2022 ');
                    color = appointment.color || '#667eea';
                  } else {
                    const event = item as CalendarEvent;
                    title = event.title;
                    subtitle = event.notes || '';
                    if (event.isNewPatientIntake) {
                      title = event.prospectName ? `${t('calendar.newPatientPrefix')}${event.prospectName}` : event.title;
                      color = '#f59e0b';
                    } else {
                      color = '#10b981';
                    }
                  }

                  return (
                    <div className="responsive-row"
                      key={item.id}
                      data-testid={`${isAppointment ? 'appointment' : 'event'}-search-result-${item.id}`}
                      onClick={() => onSelectDate(date)}
                      style={{ background: tokens.app.input, border: `1px solid ${tokens.app.border}`, borderRadius: '8px', padding: '16px', cursor: 'pointer', display: 'flex', gap: '16px', alignItems: 'center', transition: 'transform 0.1s' }}
                      onMouseEnter={event => event.currentTarget.style.transform = 'translateY(-1px)'}
                      onMouseLeave={event => event.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{ width: '4px', alignSelf: 'stretch', background: color, borderRadius: '2px' }} />
                      <div style={{ minWidth: '140px', fontSize: '0.9rem', color: tokens.app.muted, fontWeight: 500 }}>{time}</div>
                      <div style={{ flex: 1 }}>
                        <div className="responsive-row" style={{ fontSize: '1.1rem', fontWeight: 600, color: tokens.app.text, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          {title}
                          {hc && <span style={{ fontSize: '0.75rem', fontWeight: 500, background: '#1e3a5f', color: '#60a5fa', borderRadius: 4, padding: '1px 6px' }}>HC {hc}</span>}
                        </div>
                        {subtitle && <div style={{ fontSize: '0.9rem', color: tokens.app.muted }}>{subtitle}</div>}
                      </div>
                      <div style={{ color: tokens.app.primary, fontSize: '0.9rem' }}>{t('calendar.goToDate')}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
